/**
 * /settings/numbering — project-numbering scheme + dimension admin.
 *
 * Three sections on one page:
 *   1. Format template + reset strategy + starting offset
 *      Live preview of next code as you edit the template
 *   2. Declared dimensions (office, practice_area, studio, etc.)
 *      Add/rename + manage allowed values per dimension
 *   3. Token reference (built-ins + per-dimension)
 *
 * Renames preserve historical project codes (key is immutable; only
 * the label changes). Adding a new dimension requires adding it to
 * the template before generated codes start using it.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  numberingConfigQueryOptions,
  numberingDimensionsQueryOptions,
  numberingKeys,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type {
  NumberingAllocateResult,
  NumberingConfig,
  NumberingDimension,
  NumberingResetStrategy,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/settings/numbering")({
  component: NumberingSettingsPage,
});


const RESET_STRATEGIES: { value: NumberingResetStrategy; label: string; hint: string }[] = [
  { value: "never",      label: "Never",       hint: "Counter increments forever (0001, 0002, ...)" },
  { value: "annually",   label: "Annually",    hint: "Resets each calendar year. Template MUST contain {YEAR} or {YY}." },
  { value: "per_prefix", label: "Per prefix",  hint: "Resets when ANY non-{SEQ} token resolves to a different value (e.g. each office gets its own counter)." },
];


function NumberingSettingsPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const cfgQuery = useQuery(numberingConfigQueryOptions());
  const dimsQuery = useQuery(numberingDimensionsQueryOptions(false));

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-auto">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Project numbering
        </h1>
        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
          Per-entity templated codes. Customers add dimensions (office,
          practice area, studio, etc.) over time and reference them as
          tokens in the format template.
        </p>
      </div>

      <div className="flex-1 px-6 py-6 max-w-4xl space-y-6">
        <ConfigPanel
          config={cfgQuery.data ?? null}
          dimensions={dimsQuery.data ?? []}
        />

        <DimensionsPanel dimensions={dimsQuery.data ?? []} />

        <TokenReferencePanel dimensions={dimsQuery.data ?? []} />
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Config — template + reset strategy
// ──────────────────────────────────────────────────────────────
function ConfigPanel({
  config, dimensions,
}: {
  config: NumberingConfig | null;
  dimensions: NumberingDimension[];
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [template, setTemplate] = useState(config?.format_template ?? "");
  const [strategy, setStrategy] = useState<NumberingResetStrategy>(
    config?.reset_strategy ?? "per_prefix",
  );
  const [starting, setStarting] = useState(String(config?.starting_value ?? 1));

  // Sync state when config arrives async
  useMemo(() => {
    if (config) {
      setTemplate(config.format_template);
      setStrategy(config.reset_strategy);
      setStarting(String(config.starting_value));
    }
  }, [config]);

  const [previewCode, setPreviewCode] = useState<string>("");
  const [previewError, setPreviewError] = useState<string>("");

  const saveMut = useMutation({
    mutationFn: () =>
      api<NumberingConfig>("/api/numbering/config", {
        method: "PUT",
        body: {
          format_template: template,
          reset_strategy: strategy,
          starting_value: Number(starting),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: numberingKeys.all });
      toast.success("Numbering config saved");
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  // Preview = call /allocate with preview=true; pick the first value of
  // each declared dimension as a placeholder so the preview always
  // resolves something (otherwise it'd 400 on missing values).
  const previewMut = useMutation({
    mutationFn: () => {
      const dim_vals: Record<string, string> = {};
      for (const d of dimensions) {
        const v = d.values.find((v) => v.is_active);
        if (v) dim_vals[d.key] = v.id;
      }
      return api<NumberingAllocateResult>("/api/numbering/allocate", {
        method: "POST",
        body: { preview: true, dimension_values: dim_vals },
      });
    },
    onSuccess: (r) => {
      setPreviewCode(r.code);
      setPreviewError("");
    },
    onError: (err: Error) => {
      setPreviewCode("");
      setPreviewError(err.message);
    },
  });

  return (
    <section className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
      <h2 className="text-sm font-semibold text-slate-800 mb-3 dark:text-slate-200">
        Format template
      </h2>
      <div className="space-y-3">
        <TextField label="Template" size="small" fullWidth value={template}
          onChange={(e) => setTemplate(e.target.value)}
          placeholder="{COMPANY}-{YEAR}-{SEQ:4}"
          helperText="Tokens go inside {}. See the reference below for what's available."
          inputProps={{ style: { fontFamily: "monospace" } }} />
        <div className="grid grid-cols-2 gap-3">
          <TextField select label="Reset strategy" size="small"
            value={strategy}
            onChange={(e) =>
              setStrategy(e.target.value as NumberingResetStrategy)
            }
            helperText={
              RESET_STRATEGIES.find((s) => s.value === strategy)?.hint
            }>
            {RESET_STRATEGIES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField label="Starting value" size="small" type="number"
            value={starting} onChange={(e) => setStarting(e.target.value)}
            helperText="First number in each new sequence (e.g. 1, 1000)" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="contained" size="small"
            disabled={!template || saveMut.isPending}
            onClick={() => saveMut.mutate()}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
          <Button size="small" variant="outlined"
            disabled={!template || previewMut.isPending}
            onClick={() => previewMut.mutate()}>
            {previewMut.isPending ? "…" : "✨ Preview"}
          </Button>
          {previewCode && (
            <span className="ml-2 font-mono text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
              Next: {previewCode}
            </span>
          )}
          {previewError && (
            <span className="ml-2 text-xs text-red-700">{previewError}</span>
          )}
        </div>
      </div>
    </section>
  );
}


// ──────────────────────────────────────────────────────────────
// Dimensions — declare + manage values
// ──────────────────────────────────────────────────────────────
function DimensionsPanel({ dimensions }: { dimensions: NumberingDimension[] }) {
  const [creating, setCreating] = useState(false);
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex items-center mb-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          Dimensions
        </h2>
        <span className="text-xs text-slate-500 ml-2 dark:text-slate-400">
          office · practice area · studio · whatever your firm uses
        </span>
        <div className="flex-1" />
        <Button size="small" variant="outlined" onClick={() => setCreating(true)}>
          + New dimension
        </Button>
      </div>
      {dimensions.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3 dark:text-slate-400">
          No dimensions yet. Most owner-operator shops don't need any —
          add one when you grow into multi-office or multi-practice work.
        </div>
      ) : (
        <div className="space-y-3">
          {dimensions.map((d) => (
            <DimensionRow key={d.id} dimension={d} />
          ))}
        </div>
      )}
      {creating && <NewDimensionDialog onClose={() => setCreating(false)} />}
    </section>
  );
}


function DimensionRow({ dimension }: { dimension: NumberingDimension }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [renaming, setRenaming] = useState(false);
  const [newLabel, setNewLabel] = useState(dimension.label);
  const [showAddValue, setShowAddValue] = useState(false);
  const [valueCode, setValueCode] = useState("");
  const [valueLabel, setValueLabel] = useState("");

  const renameMut = useMutation({
    mutationFn: () =>
      api(`/api/numbering/dimensions/${dimension.id}`, {
        method: "PATCH",
        body: { label: newLabel },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: numberingKeys.all });
      toast.success("Dimension renamed");
      setRenaming(false);
    },
  });

  const addValueMut = useMutation({
    mutationFn: () =>
      api(`/api/numbering/dimensions/${dimension.id}/values`, {
        method: "POST",
        body: { code: valueCode.trim(), label: valueLabel.trim() },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: numberingKeys.all });
      toast.success(`Value added: ${valueCode}`);
      setValueCode(""); setValueLabel(""); setShowAddValue(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deactivateMut = useMutation({
    mutationFn: (valueId: string) =>
      api(`/api/numbering/dimensions/values/${valueId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: numberingKeys.all }),
  });

  return (
    <div className="border border-slate-200 rounded p-3 dark:border-gray-800">
      <div className="flex items-center gap-2">
        {renaming ? (
          <>
            <TextField size="small" value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)} />
            <Button size="small" onClick={() => renameMut.mutate()}>Save</Button>
            <Button size="small" onClick={() => {
              setRenaming(false);
              setNewLabel(dimension.label);
            }}>Cancel</Button>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold">{dimension.label}</span>
            <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
              key={dimension.key}
            </span>
            <Chip size="small" label={`{${dimension.token}}`}
              sx={{ fontSize: 10, height: 18, fontFamily: "monospace" }} />
            <IconButton size="small" onClick={() => setRenaming(true)} title="Rename">
              ✎
            </IconButton>
          </>
        )}
        <div className="flex-1" />
        <Button size="small" onClick={() => setShowAddValue((v) => !v)}>
          + value
        </Button>
      </div>

      {showAddValue && (
        <div className="mt-2 grid grid-cols-12 gap-2 items-center">
          <TextField label="Code" size="small" sx={{ gridColumn: "span 3" }}
            placeholder="NYC" value={valueCode}
            onChange={(e) => setValueCode(e.target.value.toUpperCase())} />
          <TextField label="Label" size="small" sx={{ gridColumn: "span 7" }}
            placeholder="New York" value={valueLabel}
            onChange={(e) => setValueLabel(e.target.value)} />
          <Button size="small" variant="contained"
            sx={{ gridColumn: "span 2" }}
            disabled={!valueCode || !valueLabel || addValueMut.isPending}
            onClick={() => addValueMut.mutate()}>
            Add
          </Button>
        </div>
      )}

      {dimension.values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {dimension.values.map((v) => (
            <Chip key={v.id} size="small"
              label={`${v.code} — ${v.label}`}
              variant={v.is_active ? "filled" : "outlined"}
              onDelete={
                v.is_active ? () => deactivateMut.mutate(v.id) : undefined
              }
              sx={{ fontSize: 11 }} />
          ))}
        </div>
      )}
    </div>
  );
}


function NewDimensionDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [token, setToken] = useState("");

  const createMut = useMutation({
    mutationFn: () =>
      api("/api/numbering/dimensions", {
        method: "POST",
        body: {
          key: key.trim(),
          label: label.trim(),
          token: token.trim() || undefined,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: numberingKeys.all });
      toast.success("Dimension created");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New dimension</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-2">
          <TextField label="Key" size="small" fullWidth value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="office"
            helperText="Stable identifier (snake_case). IMMUTABLE once data exists." />
          <TextField label="Display label" size="small" fullWidth value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Office"
            helperText="Renamable display name shown in pickers." />
          <TextField label="Token (optional)" size="small" fullWidth value={token}
            onChange={(e) => setToken(e.target.value.toUpperCase())}
            placeholder={`Defaults to UPPER(key) — e.g. ${
              key ? key.toUpperCase().replace(/\W+/g, "_") : "OFFICE"
            }`}
            helperText="Goes inside {} in the format template."
            inputProps={{ style: { fontFamily: "monospace" } }} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
          disabled={!key || !label || createMut.isPending}
          onClick={() => createMut.mutate()}>
          {createMut.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Token reference
// ──────────────────────────────────────────────────────────────
function TokenReferencePanel({ dimensions }: { dimensions: NumberingDimension[] }) {
  return (
    <section className="bg-slate-50 border border-slate-200 rounded-md p-4 dark:bg-gray-950 dark:border-gray-800">
      <h2 className="text-sm font-semibold text-slate-700 mb-2 dark:text-slate-300">
        Token reference
      </h2>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-300">
          <tr>
            <th className="text-left pb-1 w-32">Token</th>
            <th className="text-left pb-1">Resolves to</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-200 dark:border-gray-800">
            <td className="py-1 font-mono">{"{COMPANY}"}</td>
            <td className="py-1">Entity code</td>
          </tr>
          <tr className="border-t border-slate-200 dark:border-gray-800">
            <td className="py-1 font-mono">{"{YEAR}"}</td>
            <td className="py-1">4-digit year (from project bid_date if set, else today)</td>
          </tr>
          <tr className="border-t border-slate-200 dark:border-gray-800">
            <td className="py-1 font-mono">{"{YY}"}</td>
            <td className="py-1">2-digit year</td>
          </tr>
          <tr className="border-t border-slate-200 dark:border-gray-800">
            <td className="py-1 font-mono">{"{SEQ}"} / {"{SEQ:N}"}</td>
            <td className="py-1">
              Zero-padded counter. <code>{"{SEQ:4}"}</code> →{" "}
              <code>0001</code>, <code>0002</code>, …
            </td>
          </tr>
          {dimensions.map((d) => (
            <tr key={d.id} className="border-t border-slate-200 dark:border-gray-800">
              <td className="py-1 font-mono">{`{${d.token}}`}</td>
              <td className="py-1">
                {d.label} — code of the picked value
                {d.values.length > 0 && (
                  <span className="text-slate-500 ml-1 dark:text-slate-400">
                    ({d.values.slice(0, 3).map((v) => v.code).join(" / ")}
                    {d.values.length > 3 && ", …"})
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-500 mt-3 italic dark:text-slate-400">
        Examples: <code className="font-mono">{"{COMPANY}-{YY}-{SEQ:4}"}</code> →{" "}
        <code className="font-mono">ACME-26-0001</code> ·{" "}
        <code className="font-mono">{"{OFFICE}-{PRACTICE_AREA}-{YEAR}-{SEQ:4}"}</code> →{" "}
        <code className="font-mono">NYC-ARCH-2026-0001</code>
      </p>
    </section>
  );
}
